import { useEffect } from "react";

/**
 * Lightweight SEO hook for a CRA SPA — sets <title>, key meta tags,
 * canonical, OG/Twitter, hreflang, optional JSON-LD blocks, and product
 * price metas per page.
 *
 * Usage:
 *   useSeo({
 *     title: "Shop · MotoWear",
 *     description: "Browse the drop…",
 *     image: "https://…",
 *     imageAlt: "Limited-run editorial t-shirt on a model",
 *     keywords: "biker t-shirts UK, ...",
 *     path: "/shop",
 *     type: "website" | "product" | "article",
 *     priceGbp: 39.99,           // optional, stamps product:price:* metas
 *     availability: "in stock",  // optional product:availability
 *     jsonLd: [ { ...schema } ]
 *   });
 *
 * All updates are idempotent; managed tags are tagged with `data-seo-managed`
 * and replaced on next render so navigation always paints the right meta.
 */
const ORIGIN = "https://motowear.co.uk";
const TWITTER_HANDLE = "@motowearthreads";
const SITE_NAME = "MotoWear";
const DEFAULT_LOCALE = "en_GB";

function upsertMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/(name|property)="([^"]+)"/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return null;
  const hreflang = extra.hreflang;
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function useSeo({
  title,
  description,
  image,
  imageAlt,
  keywords,
  path = "",
  type = "website",
  priceGbp,
  availability,
  jsonLd = [],
  noindex = false,
}) {
  useEffect(() => {
    const fullUrl = `${ORIGIN}${path || ""}`;
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', "content", description.slice(0, 200));
      upsertMeta('meta[property="og:description"]', "content", description.slice(0, 200));
      upsertMeta('meta[name="twitter:description"]', "content", description.slice(0, 200));
    }
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "content", keywords);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "content", title);
      upsertMeta('meta[name="twitter:title"]', "content", title);
    }
    if (image) {
      upsertMeta('meta[property="og:image"]', "content", image);
      upsertMeta('meta[name="twitter:image"]', "content", image);
      if (imageAlt) {
        upsertMeta('meta[property="og:image:alt"]', "content", imageAlt);
        upsertMeta('meta[name="twitter:image:alt"]', "content", imageAlt);
      }
    }
    upsertMeta('meta[property="og:type"]', "content", type);
    upsertMeta('meta[property="og:url"]', "content", fullUrl);
    upsertMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    upsertMeta('meta[property="og:locale"]', "content", DEFAULT_LOCALE);
    upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    upsertMeta('meta[name="twitter:site"]', "content", TWITTER_HANDLE);
    // Robots — keep crawlable and rich-snippet friendly per page. Pass
    // `noindex: true` for hidden pages (e.g. the codebreaker Vault).
    upsertMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    // Product price/availability OG metas
    if (type === "product") {
      if (typeof priceGbp === "number" && priceGbp > 0) {
        upsertMeta('meta[property="product:price:amount"]', "content", priceGbp.toFixed(2));
        upsertMeta('meta[property="product:price:currency"]', "content", "GBP");
      }
      if (availability) {
        upsertMeta('meta[property="product:availability"]', "content", availability);
      }
    }
    // Canonical + hreflang (UK English + x-default for global discovery)
    upsertLink("canonical", fullUrl);
    upsertLink("alternate", fullUrl, { hreflang: "en-GB" });
    upsertLink("alternate", fullUrl, { hreflang: "x-default" });

    // JSON-LD blocks — wipe any previous ones we owned, then re-add
    document.head.querySelectorAll("script[data-seo-managed='1']").forEach((n) => n.remove());
    (jsonLd || []).filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoManaged = "1";
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
    return () => {
      document.head.querySelectorAll("script[data-seo-managed='1']").forEach((n) => n.remove());
    };
  }, [title, description, image, imageAlt, keywords, path, type, priceGbp, availability, JSON.stringify(jsonLd)]);
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${ORIGIN}${it.path}`,
    })),
  };
}

/** Convenience: structured-data block for an FAQ section. */
export function faqLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (items || []).map((it) => ({
      "@type": "Question",
      name: it.question || it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.answer || it.a || "",
      },
    })),
  };
}
